import type { LocalizedText } from "@sirdab/returns-shared";

/**
 * Portal-local copy.
 *
 * `packages/returns-shared` owns every string that crosses a boundary — an eligibility
 * reason, a policy failure, a line state. These are none of those: they are lookup-screen
 * affordances (a format hint, a resume prompt, a dead deep link) that exist only in this
 * client and never travel. They live here so the shared catalogue stays the vocabulary of
 * the domain rather than a dumping ground for one screen's microcopy, and so the rule that
 * matters — no string literal inside a component — still holds.
 *
 * Arabic is Saudi spoken ("white") Arabic, matching the shared catalogue's register.
 */
export const PORTAL_COPY = {
  /**
   * Shown under the ONE failure surface on lookup. Deliberately says nothing about which
   * half of the credential was wrong: the API answers `order_not_found` identically for a
   * bad reference and a bad phone, and this hint must not undo that.
   */
  "lookup.not_found_hint": {
    ar: "تأكد إن رقم الطلب ورقم الجوال هم نفسهم اللي بالرسالة اللي وصلتك وقت الطلب. لو الطلب انطلب برقم ثاني، جرّب ذاك الرقم.",
    en: "Check that the order number and mobile number are the ones from your order confirmation. If the order was placed with a different number, try that one.",
  },
  "lookup.phone_formats": {
    ar: "أي صيغة تمشي: 05XXXXXXXX أو 9665XXXXXXXX+.",
    en: "Any format works: 05XXXXXXXX or +9665XXXXXXXX.",
  },
  "lookup.phone_invalid": {
    ar: "الرقم مو مكتمل. رقم الجوال السعودي تسع أرقام بعد المفتاح، مثل 0512345678.",
    en: "That number looks incomplete. A Saudi mobile is nine digits after the prefix, like 0512345678.",
  },
  "lookup.resume_title": {
    ar: "نكمل من وين وقفت؟",
    en: "Pick up where you left off?",
  },
  "lookup.resume_body": {
    ar: "حفظنا بيانات آخر طلب دوّرت عليه في هذي الجلسة، عشان ما تعيد كتابتها.",
    en: "We kept the details of the order you looked up in this session, so you do not have to type them again.",
  },
  "action.resume_lookup": {
    ar: "كمّل على طلبي",
    en: "Continue with my order",
  },
  "action.use_other_order": {
    ar: "طلب ثاني",
    en: "A different order",
  },

  /* --- Phone and code sign-in (SAI-1344) --- */
  "code.sent_to": {
    ar: "أرسلنا الرمز إلى",
    en: "We sent the code to",
  },
  "code.resent": {
    ar: "أرسلنا لك رمز جديد.",
    en: "A new code is on its way.",
  },

  /* --- Deep link (`/:merchantSlug/returns/r/:token`) --- */
  "link.resolving": {
    ar: "لحظة، نجيب إرجاعك…",
    en: "One moment, fetching your return…",
  },
  /**
   * The token failed. The endpoint answers the same way for expired, revoked and never
   * existed, so this copy says "the link" and never says why — the reason is not ours to
   * guess and the endpoint deliberately does not tell us.
   */
  "link.failed": {
    ar: "الرابط ما عاد يشتغل. اكتب رقم الطلب ورقم الجوال وبنلقى طلبك على طول.",
    en: "That link no longer works. Enter your order number and mobile number and we will find your order.",
  },
  /** The link could not be read at all (offline, a server hiccup). Nothing is known about it. */
  "link.unreachable": {
    ar: "ما قدرنا نفتح الرابط الحين. تأكد من الاتصال وجرّب مرة ثانية.",
    en: "We could not open this link right now. Check your connection and try again.",
  },
  /** Under the phone field on cancel, when a track link opened the return. */
  "cancel.phone_hint": {
    ar: "اكتب رقم الجوال اللي طلبت فيه عشان نتأكد إنه إرجاعك.",
    en: "Enter the mobile number on the order so we know this return is yours.",
  },
  /** Under the "requested" step: the shopper's own note, quoted back to them. */
  "track.your_words": {
    ar: "كلامك",
    en: "Your words",
  },
  "track.photos_attached": {
    ar: "الصور اللي أرفقتها",
    en: "Photos you attached",
  },
} as const satisfies Record<string, LocalizedText>;

export type PortalCopyKey = keyof typeof PORTAL_COPY;
